import {
  Building2,
  CalendarCheck,
  CircleDollarSign,
  Globe,
  HandCoins,
  LayoutDashboard,
  Megaphone,
  Receipt,
  ReceiptText,
  Settings2,
  ShieldCheck,
  Star,
  Target,
  UserPlus,
  Users,
  UtensilsCrossed,
  Wallet,
  Wrench,
  Zap,
  FileBarChart,
  CreditCard,
} from 'lucide-react';
import type { NavGroup, PaletteCommand } from './types';

/**
 * Property owner / manager app. Organised by job, not by feature:
 * what needs me → who is in which bed → get paid → fill beds → talk to people → configure.
 * Sub-sections of an item are tabs inside its page, never a second sidebar.
 */
export const ownerNav: readonly NavGroup[] = [
  {
    id: 'today',
    items: [{ id: 'today', label: 'Today', to: '/app', icon: LayoutDashboard, end: true, tab: true, keywords: ['home', 'dashboard', 'overview'] }],
  },
  {
    id: 'operate',
    label: 'Operate',
    items: [
      { id: 'residents', label: 'Residents', to: '/app/residents', icon: Users, tab: true, keywords: ['tenants', 'people', 'kyc', 'moved out'] },
      { id: 'property', label: 'Property', to: '/app/property', icon: Building2, tab: true, keywords: ['rooms', 'beds', 'floors', 'vacant'] },
      { id: 'issues', label: 'Issues', to: '/app/issues', icon: Wrench, keywords: ['complaints', 'maintenance', 'tickets', 'sla'] },
      { id: 'mess', label: 'Mess', to: '/app/mess', icon: UtensilsCrossed, keywords: ['food', 'meals', 'menu', 'cutoff'] },
      { id: 'utilities', label: 'Utilities', to: '/app/utilities', icon: Zap, keywords: ['electricity', 'meter', 'readings', 'bills'] },
    ],
  },
  {
    id: 'money',
    label: 'Money',
    items: [
      { id: 'dues', label: 'Dues', to: '/app/dues', icon: Receipt, tab: true, tabLabel: 'Money', keywords: ['rent', 'collect payment', 'overdue', 'invoice'] },
      { id: 'collected', label: 'Collected', to: '/app/collected', icon: HandCoins, keywords: ['payments', 'receipts', 'collections'] },
      { id: 'expenses', label: 'Expenses', to: '/app/expenses', icon: ReceiptText, keywords: ['spend', 'vendor', 'voucher'] },
      { id: 'reports', label: 'Reports', to: '/app/reports', icon: FileBarChart, keywords: ['export', 'p&l', 'ledger', 'statements'] },
    ],
  },
  {
    id: 'growth',
    label: 'Growth',
    items: [
      { id: 'leads', label: 'Leads', to: '/app/leads', icon: Target, keywords: ['enquiries', 'prospects', 'walk-in', 'pipeline'] },
      { id: 'bookings', label: 'Bookings', to: '/app/bookings', icon: CalendarCheck, keywords: ['token', 'key handover', 'move in'] },
      { id: 'listings', label: 'Listings', to: '/app/listings', icon: Globe, keywords: ['microsite', 'marketing', 'photos'] },
      { id: 'reviews', label: 'Reviews', to: '/app/reviews', icon: Star, keywords: ['surveys', 'feedback', 'ratings'] },
    ],
  },
  {
    id: 'communicate',
    label: 'Communicate',
    items: [{ id: 'communicate', label: 'Communicate', to: '/app/communicate', icon: Megaphone, keywords: ['announcements', 'whatsapp', 'broadcast', 'agreements', 'notices'] }],
  },
  {
    id: 'admin',
    label: 'Admin',
    items: [
      { id: 'team', label: 'Team', to: '/app/team', icon: ShieldCheck, keywords: ['staff', 'permissions', 'warden', 'roles'] },
      { id: 'setup', label: 'Setup', to: '/app/setup', icon: Settings2, keywords: ['settings', 'rules', 'rent defaults', 'sla'] },
      { id: 'billing', label: 'Billing', to: '/app/billing', icon: CreditCard, keywords: ['subscription', 'wallet', 'bank', 'plan'] },
    ],
  },
];

/** "+ New" and palette shortcuts. Each destination is a real route that opens the right form. */
export const ownerCommands: readonly PaletteCommand[] = [
  { id: 'new-resident', label: 'Add resident', to: '/app/residents?new=1', icon: UserPlus, keywords: ['onboard', 'tenant', 'new'] },
  { id: 'new-lead', label: 'Add lead', to: '/app/leads?new=1', icon: Target, keywords: ['enquiry', 'walk-in'] },
  { id: 'collect', label: 'Collect payment', to: '/app/dues?collect=1', icon: Wallet, keywords: ['receive', 'rent', 'cash', 'upi'] },
  { id: 'new-due', label: 'Add due', to: '/app/dues?new=1', icon: CircleDollarSign, keywords: ['charge', 'invoice'] },
  { id: 'new-expense', label: 'Add expense', to: '/app/expenses?new=1', icon: ReceiptText, keywords: ['spend', 'voucher'] },
  { id: 'new-issue', label: 'Raise issue', to: '/app/issues?new=1', icon: Wrench, keywords: ['complaint', 'ticket'] },
  { id: 'new-announcement', label: 'Send announcement', to: '/app/communicate?new=1', icon: Megaphone, keywords: ['broadcast', 'notice', 'whatsapp'] },
];
